import type { StatusTone } from '../contracts/common';
import type { InvitationStatus } from '../contracts/team';
import {
  type AccessLevel,
  type BooleanFlag,
  type DataScope,
  type MembershipStatus,
  MODULES,
  PERMISSION_TEMPLATE_DEFAULTS,
  type PermissionTemplate,
  type Role,
} from '../enums';
import { MODULE_LABELS } from './identity';

/** Zespół, role i uprawnienia (PERMISSIONS §2–§3): opisy dla ekranów Ustawienia → Zespół. */

/** Krótki opis roli (karty wyboru roli w „Zaproś osobę”, Figma 3035:4324). */
export const TEAM_ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Pełny dostęp, abonament i własność workspace’u.',
  admin: 'Pełny dostęp i zespół.',
  manager: 'Wszystko oprócz osób i ról.',
  user: 'Wybrane moduły i klienci.',
  client: 'Tylko portal zgłoszeń swojego klienta.',
};

export const TEAM_DATA_SCOPE_DESCRIPTIONS: Record<DataScope, string> = {
  all: 'Widzi wszystkich klientów i ich projekty.',
  selected: 'Tylko klienci wybrani z listy.',
  assigned: 'Klienci, których jest opiekunem, i projekty, w których jest członkiem.',
};

/** Opisy przełączników (Uprawnienia specjalne, PERMISSIONS §3.3). */
export const TEAM_FLAG_DESCRIPTIONS: Record<Exclude<BooleanFlag, 'manageMembers'> | 'approvesAi', string> = {
  seesRates: 'Bez tego widzi tylko godziny: bez stawek i kwot, także w eksportach.',
  sendsEmails: 'Może zatwierdzić wysłanie maila do klienta.',
  approvesAi: 'Które akcje AI może zatwierdzać. Pozostałe trafiają do Managera albo Admina.',
  preparesBilling: 'Tworzy zestawienia czasu dla klientów.',
  issuesInvoices: 'Może zatwierdzić wystawienie faktury.',
  deploysProduction: 'Może zatwierdzić wdrożenie na produkcję.',
  exportsData: 'Pobiera raporty i listy jako CSV i PDF.',
  vaultAccess: 'Podgląd haseł z sejfu zasobów, które widzi. Każdy podgląd trafia do historii.',
};

/** Notka przy „Widzi dostępy (sejf)”. */
export const TEAM_VAULT_NOTE =
  'Będzie wymagać weryfikacji dwuetapowej (wkrótce). Dostęp do sejfu pojedynczego zasobu nadasz też niżej, w sekcji dostępu do zasobów.';

export const TEAM_AI_LIMIT_NOTE = 'Po przekroczeniu AI się zatrzyma i zapyta Ciebie.';

/** Podpowiedź przy limicie AI osoby z szablonem Partner (DECISIONS 2026-10-08 [S] „Reguła partnerów”). */
export const TEAM_AI_LIMIT_PARTNER_NOTE =
  "Partner korzysta z AI we własnym workspace'ie (własny klucz i budżet). 0 zł = AI tutaj wyłączone; wpisz kwotę, jeśli świadomie udostępniasz mu AI na swoim kluczu.";

export const TEAM_PERMISSION_TEMPLATE_DESCRIPTIONS: Record<PermissionTemplate, string> = {
  developer:
    'Zadania, projekty, czas, wiedza i agenci; podgląd klientów i utrzymania. Tylko przypisani klienci, bez kwot.',
  support: 'Inbox, klienci, zadania i czas. Wybrani klienci, wysyła maile, bez kwot.',
  accounting: 'Rozliczenia; podgląd czasu, klientów i utrzymania. Wszyscy klienci, z kwotami.',
  partner: 'Wspólne projekty z inną firmą: projekty, zadania, czas i wiedza tylko tam, gdzie jest członkiem. Bez kwot.',
};

/** „Zadania, Projekty, Czas, Wiedza, Agenci · bez kwot” (blok szablonu w „Zaproś osobę”). */
export function templateSummary(template: PermissionTemplate): string {
  const def = PERMISSION_TEMPLATE_DEFAULTS[template];
  const edit = MODULES.filter((m) => def.modules[m] === 'edit').map((m) => MODULE_LABELS[m]);
  return `${edit.join(', ')} · ${def.flags.seesRates ? 'z kwotami' : 'bez kwot'}`;
}

export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: 'Zaproszenie wysłane',
  expired: 'Zaproszenie wygasło',
};

export const INVITATION_STATUS_TONES: Record<InvitationStatus, StatusTone> = {
  pending: 'warning',
  expired: 'neutral',
};

export const MEMBERSHIP_STATUS_TONES: Record<MembershipStatus, StatusTone> = {
  active: 'success',
  disabled: 'neutral',
};

/** Krótka etykieta poziomu w tabeli modułów (Segmented). */
export const TEAM_ACCESS_LEVEL_SHORT: Record<AccessLevel, string> = {
  none: 'Brak',
  view: 'Podgląd',
  edit: 'Edycja',
};

/** Komórka tabeli „Role i dostęp”: true = tak, false = nie, tekst = opis. */
export type RoleMatrixCell = boolean | string;

/** Tabela „Role i dostęp” (Figma 3034:4305). */
export const TEAM_ROLE_MATRIX: {
  label: string;
  admin: RoleMatrixCell;
  manager: RoleMatrixCell;
  user: RoleMatrixCell;
}[] = [
  { label: 'Własni klienci, projekty, zadania, czas, wiedza', admin: true, manager: true, user: 'wg uprawnień' },
  { label: 'Klienci innych osób', admin: 'wszyscy', manager: 'wszyscy', user: 'wybrani' },
  { label: 'Akceptacje działań AI', admin: 'wszystkie', manager: 'wszystkie', user: 'swoje, jeśli może' },
  { label: 'Zestawienia i faktury', admin: 'wystawia', manager: 'wystawia', user: 'wg uprawnień' },
  { label: 'Własna skrzynka i kalendarz', admin: true, manager: true, user: true },
  { label: 'Integracje firmy i limity AI', admin: true, manager: true, user: false },
  { label: 'Zapraszanie osób, role, uprawnienia', admin: true, manager: false, user: false },
];
